import { useRef, useState } from "react";
import {
  Download, Mail, Copy, Printer,
  FileImage, FileText as FileTextIcon, Edit, X,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { toast } from "sonner";
import { copyToClipboard } from "../../../utils/clipboard";

export function LetterPreview({ letter, companyData, onClose, onEdit }) {
  const letterRef   = useRef(null);
  const [exporting, setExporting] = useState(false);

  /* ── Helpers ── */
  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
      : new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  const refId = () => {
    if (letter.id) return String(letter.id).padStart(6, "0");
    return "LTR-" + Date.now();
  };

  /* ── Actions ── */
  const handleCopy = async () => {
    const text = `${letter.subject}\n\n${letter.content}`;
    const ok = await copyToClipboard(text);
    ok ? toast.success("Copied to clipboard!") : toast.error("Failed to copy");
  };

  const handlePrint = () => window.print();

  const handleDownloadJPG = async () => {
    if (!letterRef.current || exporting) return;
    setExporting(true);
    toast.info("Generating image…");
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(letterRef.current, {
        backgroundColor: "var(--em-bg-card)",
        scale: 2,
        useCORS: true,
        allowTaint: false,
      });
      canvas.toBlob(
        (blob) => {
          if (!blob) return toast.error("Failed to generate image");
          const url  = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href     = url;
          link.download = `letter_${letter.id || Date.now()}.jpg`;
          link.click();
          URL.revokeObjectURL(url);
          toast.success("JPG downloaded!");
        },
        "image/jpeg",
        0.95
      );
    } catch (err) {
      toast.error("Image export failed: " + err.message);
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!letterRef.current || exporting) return;
    setExporting(true);
    toast.info("Generating PDF…");
    try {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF }   = await import("jspdf");

      const canvas  = await html2canvas(letterRef.current, {
        backgroundColor: "var(--em-bg-card)",
        scale: 2,
        useCORS: true,
        allowTaint: false,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf     = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

      const pageW   = 210;
      const pageH   = 297;
      const imgH    = (canvas.height * pageW) / canvas.width;
      let posY      = 0;
      let remaining = imgH;

      pdf.addImage(imgData, "PNG", 0, posY, pageW, imgH);
      remaining -= pageH;
      while (remaining > 0) {
        posY -= pageH;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, posY, pageW, imgH);
        remaining -= pageH;
      }

      pdf.save(`letter_${letter.id || Date.now()}.pdf`);
      toast.success("PDF downloaded!");
    } catch (err) {
      toast.error("PDF export failed: " + err.message);
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadHTML = () => {
    setExporting(true);
    try {
      const company = companyData || {};
      const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; padding: 40px; }
    .header { border-bottom: 2px solid #4F46E5; padding-bottom: 16px; margin-bottom: 24px; }
    .header h1 { margin: 0 0 4px; font-size: 22px; }
    .header p  { margin: 2px 0; color: #6B7280; font-size: 13px; }
    .ref     { text-align: right; color: #6B7280; font-size: 13px; margin-bottom: 16px; }
    .subject { margin-bottom: 16px; font-size: 14px; }
    .body    { white-space: pre-wrap; font-size: 14px; line-height: 1.8; }
    .sig     { margin-top: 60px; font-size: 14px; }
    .footer  { margin-top: 40px; border-top: 1px solid #E5E7EB; padding-top: 12px;
               text-align: center; color: #9CA3AF; font-size: 12px; }
  </style>
</head>
<body>
  <div class="header">
    <h1>${company.name || "Company Name"}</h1>
    ${company.address ? `<p>${company.address}</p>` : ""}
    ${company.email   ? `<p>Email: ${company.email}</p>` : ""}
    ${company.phone   ? `<p>Phone: ${company.phone}</p>` : ""}
  </div>
  <div class="ref">
    <p>Date: ${formatDate(letter.created_at)}</p>
    <p>Ref: LTR-${refId()}</p>
  </div>
  <div class="subject"><strong>Subject:</strong> ${letter.subject}</div>
  <div class="body">${letter.content}</div>
  <div class="sig">
    <p>${letter.placeholder_data?.signatoryName || "Authorised Signatory"}</p>
    <p>${letter.placeholder_data?.signatoryTitle || "HR Department"}</p>
    <p>${company.name || "Company Name"}</p>
  </div>
  <div class="footer">
    <p>This is a computer-generated letter.</p>
    ${company.gstNo ? `<p>GST: ${company.gstNo}</p>` : ""}
  </div>
</body>
</html>`;
      const blob = new Blob([html], { type: "text/html" });
      const url  = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href     = url;
      link.download = `letter_${letter.id || Date.now()}.html`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded as HTML (open in Word for DOCX)");
    } catch (err) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleEmail = () => {
    const dest = letter.placeholder_data?.employeeEmail || letter.employee_email || "";
    window.location.href = `mailto:${dest}?subject=${encodeURIComponent(
      letter.subject
    )}&body=${encodeURIComponent(letter.content)}`;
    toast.info("Opening email client…");
  };

  /* ── Render ── */
  return (
    <div className="lt-preview-wrapper">
      {/* Action bar */}
      <div className="lt-preview-actions print:hidden">
        <div className="lt-preview-actions-left">
          {onEdit && (
            <button className="lt-icon-action-btn" onClick={onEdit}>
              <Edit size={15} /> Edit
            </button>
          )}
          <button className="lt-icon-action-btn" onClick={handleCopy}>
            <Copy size={15} /> Copy
          </button>
          <button className="lt-icon-action-btn" onClick={handlePrint}>
            <Printer size={15} /> Print
          </button>
          <button className="lt-icon-action-btn" onClick={handleEmail}>
            <Mail size={15} /> Email
          </button>
        </div>
        <div className="lt-preview-actions-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="lt-btn-primary" disabled={exporting}>
                <Download size={15} /> Download
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Choose Format</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleDownloadJPG}>
                <FileImage size={14} style={{ marginRight: 8 }} /> JPG Image
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDownloadPDF}>
                <FileTextIcon size={14} style={{ marginRight: 8 }} /> PDF Document
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDownloadHTML}>
                <FileTextIcon size={14} style={{ marginRight: 8 }} /> HTML / DOCX
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {onClose && (
            <button className="lt-icon-action-btn" onClick={onClose}>
              <X size={15} /> Close
            </button>
          )}
        </div>
      </div>

      {/* A4 Letter */}
      <div className="lt-a4-wrap">
        <div ref={letterRef} className="lt-a4">
          {/* Company header */}
          <div className="lt-a4-header">
            <div className="lt-a4-company">
              {(companyData?.logoUrl || letter.companyLogo) && (
                <img
                  src={companyData?.logoUrl || letter.companyLogo}
                  alt="Logo"
                  className="lt-a4-logo"
                  crossOrigin="anonymous"
                />
              )}
              <h1 className="lt-a4-company-name">
                {companyData?.name || "Company Name"}
              </h1>
              <div className="lt-a4-company-info">
                {companyData?.address && <p>{companyData.address}</p>}
                {companyData?.country && <p>{companyData.country}</p>}
                {companyData?.websiteUrl && <p>{companyData.websiteUrl}</p>}
                {companyData?.email && <p>Email: {companyData.email}</p>}
                {companyData?.phone && <p>Phone: {companyData.phone}</p>}
              </div>
            </div>
            <div className="lt-a4-ref">
              <p>Date: {formatDate(letter.created_at)}</p>
              <p>Ref: LTR-{refId()}</p>
            </div>
          </div>

          <hr className="lt-a4-divider" />

          {/* Subject */}
          <div className="lt-a4-subject">
            <strong>Subject:</strong> {letter.subject}
          </div>

          {/* Body */}
          <div className="lt-a4-body">
            <pre className="lt-a4-pre">{letter.content}</pre>
          </div>

          {/* Signature */}
          <div className="lt-a4-signature">
            {companyData?.signatureUrl && (
              <img
                src={companyData.signatureUrl}
                alt="Signature"
                className="lt-a4-sig-img"
                crossOrigin="anonymous"
              />
            )}
            <p className="lt-a4-sig-name">
              {letter.placeholder_data?.signatoryName || "Authorised Signatory"}
            </p>
            <p className="lt-a4-sig-title">
              {letter.placeholder_data?.signatoryTitle || "HR Department"}
            </p>
            <p className="lt-a4-sig-company">{companyData?.name || "Company Name"}</p>
          </div>

          {/* Footer */}
          <div className="lt-a4-footer">
            <p>This is a computer-generated letter and does not require a physical signature.</p>
            {companyData?.gstNo && <p>GST No: {companyData.gstNo}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
